// Pairs what the pipeline extracted from a document against what is on file.
//
// This is the comparison the flagged-mismatch review turns on: the new app has
// the driver enter their details and the extraction check them, so a reviewer
// needs both values side by side and a way to correct the one that is wrong.
// Drivers from the old app entered nothing, so "on file" is often empty and the
// extracted value is what gets typed in.
//
// The pairing is per document type, because each document corroborates different
// facts and those facts live in different stores — the license against the Mongo
// profile, the registration against the driver's vehicle row. Nothing here writes;
// it only says where a value came from and whether it can be corrected.

// moment, not dayjs: dayjs is not a dependency here — this repo's date library is
// moment/moment-timezone (App.js, ui/core/DatePicker, the station and promotion widgets).
import moment from 'moment';

/** Where an on-file value lives, which decides the write path. */
export const DRIVER = 'driver';
export const VEHICLE = 'vehicle';
/**
 * The document row itself — currently just the expiry.
 *
 * Written through the decide endpoint with no status, which means "correct the
 * fields, do not decide". That matters for the case this exists for: an EXPIRED row
 * is expired *because* of the date, and EXPIRED is derived so it cannot be restated
 * as a status. Sending a decidable one instead would un-approve or re-reject the
 * document as a side effect of fixing a typo.
 */
export const DOCUMENT = 'document';

/**
 * VIN is deliberately absent from every list.
 *
 * It still matters — it is how the right entry is picked out of an insurance
 * policy covering several vehicles — but it is not shown as a row to compare or
 * correct. `insuranceEntryFor` uses it; reviewers do not see it.
 */
const PAIRINGS = {
  LICENSE: [
    { key: 'dob', label: 'Date of birth', source: DRIVER, path: 'dob', editable: true, isDate: true },
    { key: 'sex', label: 'Sex', source: DRIVER, path: 'gender', editable: true },
    { key: 'addressLine1', label: 'Address', source: DRIVER, path: 'address.line1', editable: true },
    { key: 'addressLine2', label: 'Address line 2', source: DRIVER, path: 'address.line2', editable: true },
    { key: 'city', label: 'City', source: DRIVER, path: 'address.city', editable: true },
    { key: 'state', label: 'State', source: DRIVER, path: 'address.state', editable: true },
    { key: 'postalCode', label: 'Postal code', source: DRIVER, path: 'address.postal_code', editable: true },
    { key: 'expirationDate', label: 'Expires', source: DOCUMENT, path: 'expirationDate', editable: true, isDate: true },
  ],
  REGISTRATION: [
    { key: 'licensePlate', label: 'Plate', source: VEHICLE, path: 'licensePlate', editable: true },
    { key: 'expirationDate', label: 'Expires', source: DOCUMENT, path: 'expirationDate', editable: true, isDate: true },
    // Nothing stores which state a vehicle is registered in, so this is extracted
    // only. Shown anyway — a Florida registration on a Texas driver is worth
    // seeing even with nothing to compare it to.
    { key: 'state', label: 'Registered in', source: null, path: null, editable: false },
  ],
  INSURANCE: [
    { key: 'expirationDate', label: 'Expires', source: DOCUMENT, path: 'expirationDate', editable: true, isDate: true },
    { key: 'effectiveDate', label: 'Effective', source: null, path: null, editable: false, isDate: true },
    // From the shared catalog row, so read-only: correcting them here would edit
    // every driver of the same model. A wrong catalog entry is a catalog fix.
    { key: 'year', label: 'Year', source: VEHICLE, path: 'year', editable: false },
    { key: 'make', label: 'Make', source: VEHICLE, path: 'make', editable: false },
    { key: 'model', label: 'Model', source: VEHICLE, path: 'model', editable: false },
    { key: 'trim', label: 'Trim', source: null, path: null, editable: false },
    { key: 'bodyType', label: 'Body type', source: null, path: null, editable: false },
    { key: 'classification', label: 'Class', source: null, path: null, editable: false },
  ],
};

const read = (object, path) =>
  path ? path.split('.').reduce((value, key) => (value == null ? value : value[key]), object) : undefined;

/** Text for display, with anything empty collapsing to null. */
const text = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

/**
 * A date-valued field, as YYYY-MM-DD.
 *
 * Which rows are dates is declared on the PAIRING (`isDate`), not guessed from the value.
 * The first version sniffed magnitude — "a number over 1e11 is epoch ms" — which is wrong
 * for anyone born between roughly Nov 1966 and Mar 1973: their epoch value is under 1e11
 * in absolute terms, so it fell through to String() and a reviewer saw a raw number plus a
 * false mismatch. That is drivers currently in their fifties, not a rare edge.
 *
 * Accepts what the API actually sends for these: a Date, epoch MILLISECONDS (Mongo stores
 * `dob` as a Date and it crosses JSON as a number), or an ISO string.
 *
 * UTC on purpose. A date-only value stored at UTC midnight slides to the previous day when
 * parsed in a negative offset, and for a date of birth that is a wrong answer rather than a
 * cosmetic one.
 *
 * Anything unparseable falls back to the raw text rather than rendering "Invalid date":
 * showing a reviewer what is actually stored beats hiding it behind an error string.
 */
const dateText = (value) => {
  if (value === null || value === undefined || value === '') return null;
  // Epoch millis held as DIGITS is a fourth shape, and the one moment cannot take: it
  // delegates an unrecognised string to `new Date()`, and `new Date("1044230400000")`
  // is Invalid — so the fallback below printed the raw number next to a readable
  // extracted date, which reads as a mismatch when the two are the same day. Mongo's
  // `dob` crosses GraphQL as a string for these. Same coercion as `normalizeDob` in
  // hitch-services, which fixed the identical shape on the matching side.
  const raw = value instanceof Date ? value.getTime() : value;
  const parsed = moment.utc(typeof raw === 'string' && /^-?\d+$/.test(raw.trim()) ? Number(raw) : raw);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : String(value);
};

/**
 * Whether two values agree.
 *
 * Loose on purpose. A scan reads "AUSTIN" where the profile says "Austin", and a
 * date arrives as an ISO timestamp on one side and YYYY-MM-DD on the other —
 * flagging those as mismatches would bury the real ones. Only compared when both
 * sides have a value; a missing one is not a disagreement, it is a gap.
 */
const agrees = (extracted, onFile) => {
  if (extracted === null || onFile === null) return null;
  const a = extracted.trim().toLowerCase();
  const b = onFile.trim().toLowerCase();
  if (a === b) return true;
  // Dates: compare the day, not the formatting.
  const day = (v) => (/^\d{4}-\d{2}-\d{2}/.test(v) ? v.slice(0, 10) : null);
  if (day(a) && day(b)) return day(a) === day(b);
  // Plates and postal codes differ by punctuation more often than by value.
  return a.replace(/[^a-z0-9]/g, '') === b.replace(/[^a-z0-9]/g, '');
};

/**
 * The entry of an insurance extraction that describes this vehicle.
 *
 * A policy can cover several vehicles, so entries are matched on VIN. Falls back
 * to the only entry when there is just one — a single-vehicle policy whose VIN
 * the model misread should still line up — and otherwise to nothing, because
 * showing another vehicle's cover as this one's is worse than showing none.
 */
export const insuranceEntryFor = (entries, vin) => {
  if (!entries?.length) return null;
  if (vin) {
    const normalized = vin.trim().toLowerCase();
    const match = entries.find((entry) => entry.label?.trim().toLowerCase() === normalized);
    if (match) return match;
  }
  return entries.length === 1 ? entries[0] : null;
};

/**
 * Which entry of an extraction describes this subject.
 *
 * One rule, used for the consensus entry AND for each model's own — they must pick
 * the same vehicle out of a multi-vehicle policy or the per-field candidates would be
 * comparing different cars.
 */
export const entryFor = ({ documentType, entries, vin }) =>
  documentType === 'INSURANCE' ? insuranceEntryFor(entries, vin) : entries?.[0] || null;

/**
 * The DISTINCT values the models read for one field, most-agreed first.
 *
 * Grouped by value rather than listed per model, because the reviewer's question is
 * "what are the candidate answers", not "what did each model say" — three models
 * returning the same date is one answer with three votes, and showing it as three
 * rows makes a unanimous field look contested.
 *
 * Values are compared with the same loose `agrees` used against the record, so
 * "AUSTIN" and "Austin" are one candidate. The first-seen spelling wins, which is
 * arbitrary but stable, and the vote count is what actually decides.
 */
const candidatesFor = ({ models, documentType, vin, key }) => {
  const groups = [];
  (models || [])
    .filter((model) => model.ok)
    .forEach((model) => {
      const entry = entryFor({ documentType, entries: model.entries, vin });
      const value = text(entry?.fields?.find((field) => field.key === key)?.value);
      if (!value) return;
      const existing = groups.find((group) => agrees(group.value, value));
      if (existing) {
        existing.models.push(model.model);
      } else {
        groups.push({ value, models: [model.model] });
      }
    });
  return groups.sort((a, b) => b.models.length - a.models.length);
};

/**
 * One row per fact this document corroborates.
 *
 * `onFile` is null both when nothing is stored and when the field has no store at
 * all; `source` is what tells those apart, and the caller shows them differently —
 * "not filled in" and "we do not keep this" are different situations.
 *
 * `candidates` carries the per-model answers onto the row itself. A split run
 * publishes NO consensus, so `extracted` is null for exactly the fields a reviewer
 * most needs to see — the answers were only ever in a separate per-model section
 * underneath, which meant reading two lists and matching them up by eye.
 */
export const pairFields = ({ documentType, entry, models, vin, driver, vehicle, documentRow }) => {
  const pairings = PAIRINGS[documentType] || [];
  const extracted = new Map((entry?.fields || []).map((field) => [field.key, field.value]));
  const stores = { [DRIVER]: driver, [VEHICLE]: vehicle, [DOCUMENT]: documentRow };

  return pairings.map((pairing) => {
    // Both sides through the same formatter, so a date compares as a date rather than one
    // side's ISO string against the other's epoch number.
    const format = pairing.isDate ? dateText : text;
    const extractedValue = format(extracted.get(pairing.key));
    const onFileValue = pairing.source ? format(read(stores[pairing.source], pairing.path)) : null;
    const perModel = candidatesFor({ models, documentType, vin, key: pairing.key });
    // Fall back to the consensus value so a row always has its extracted answer as a
    // choice, whether it came from the per-model breakdown or from a run that only
    // published the agreed value. One choice is still a choice — picking it is how a
    // reviewer copies it onto the record.
    const candidates = perModel.length || !extractedValue ? perModel : [{ value: extractedValue, models: [] }];
    return {
      ...pairing,
      extracted: extractedValue,
      onFile: onFileValue,
      agrees: agrees(extractedValue, onFileValue),
      candidates,
      // The reviewer has to choose only when the models actually returned different
      // answers. One candidate with three votes is not a choice, whether or not the
      // run as a whole was marked split — a run splits on ANY field, and the fields
      // that were unanimous within it should still read as settled.
      contested: candidates.length > 1,
    };
  });
};

/** Rows a reviewer should look at first: both sides present and disagreeing. */
export const mismatchCount = (rows) => rows.filter((row) => row.agrees === false).length;

/** Rows where the models returned more than one answer, so a choice is required. */
export const contestedCount = (rows) => rows.filter((row) => row.contested).length;

/**
 * Rows where both sides actually had a value, so the comparison meant something.
 *
 * "All match" is a claim about a comparison, and a contested field has no consensus
 * value to compare — its `agrees` is null, not false. Counting only mismatches
 * therefore reported "all match" on a run whose models flatly disagreed, next to a
 * "models disagree" pill saying the opposite. Both were technically true and together
 * they said nothing.
 */
export const comparedCount = (rows) => rows.filter((row) => row.agrees !== null).length;

/**
 * Rows whose extracted value could be copied onto the record.
 *
 * Editable, extracted, and either empty on file or disagreeing — which is exactly
 * the old-app case: the driver entered nothing and the scan has the answer.
 */
export const fillableRows = (rows) =>
  rows.filter((row) => row.editable && row.extracted && row.agrees !== true);
